import { Styles } from './styleTypes';

/**
 * Default inline styles for the DatabaseViewer component
 * These styles provide a clean, modern look out of the box
 * Uses CSS custom properties for theming and dark mode support
 */
export const defaultStyles: Styles = {
  container: {
    color: 'var(--tlens-text-primary, #333)',
    maxWidth: '100%',
    margin: 0,
    padding: 'var(--tlens-spacing-md, 1rem)',
  },
  loading: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 'var(--tlens-spacing-lg, 2rem)',
    gap: 'var(--tlens-spacing-md, 1rem)',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '4px solid var(--tlens-bg-spinner-track, #f3f3f3)',
    borderTop: '4px solid var(--tlens-primary, #3498db)',
    borderRadius: '50%',
    animation: 'spin var(--tlens-animation-duration, 1s) linear infinite',
  },
  error: {
    padding: 'var(--tlens-spacing-md, 1rem)',
    backgroundColor: 'var(--tlens-bg-error, #fee)',
    border: '1px solid var(--tlens-border-error, #fcc)',
    borderRadius: 'var(--tlens-radius, 4px)',
    color: 'var(--tlens-error, #c33)',
  },
  retry: {
    marginTop: 'var(--tlens-spacing-xs, 0.5rem)',
    padding: 'var(--tlens-spacing-xs, 0.5rem) var(--tlens-spacing-md, 1rem)',
    backgroundColor: 'var(--tlens-error, #c33)',
    color: 'white',
    border: 'none',
    borderRadius: 'var(--tlens-radius, 4px)',
    cursor: 'pointer',
  },
  filter: {
    marginBottom: 'var(--tlens-spacing-md, 1rem)',
  },
  filterInput: {
    width: '100%',
    padding: 'var(--tlens-spacing-xs, 0.5rem)',
    border: '1px solid var(--tlens-border, #ddd)',
    borderRadius: 'var(--tlens-radius, 4px)',
    fontSize: 'var(--tlens-font-size-base, 1rem)',
  },
  tableWrapper: {
    overflowX: 'auto',
    border: '1px solid var(--tlens-border, #ddd)',
    borderRadius: 'var(--tlens-radius, 4px)',
    marginBottom: 'var(--tlens-spacing-md, 1rem)',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    backgroundColor: 'var(--tlens-bg-white, white)',
  },
  th: {
    padding: 'var(--tlens-spacing-sm, 0.75rem)',
    textAlign: 'left',
    borderBottom: '1px solid var(--tlens-border, #ddd)',
    backgroundColor: 'var(--tlens-bg-header, #f8f9fa)',
    fontWeight: 600,
    position: 'sticky',
    top: 0,
  },
  td: {
    padding: 'var(--tlens-spacing-sm, 0.75rem)',
    textAlign: 'left',
    borderBottom: '1px solid var(--tlens-border, #ddd)',
  },
  empty: {
    textAlign: 'center',
    padding: 'var(--tlens-spacing-lg, 2rem)',
    color: 'var(--tlens-text-secondary, #666)',
  },
  sortable: {
    cursor: 'pointer',
    userSelect: 'none',
  },
  sorted: {
    cursor: 'pointer',
    userSelect: 'none',
    backgroundColor: 'var(--tlens-bg-sorted, #e9ecef)',
  },
  pagination: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 'var(--tlens-spacing-xs, 0.5rem)',
    marginBottom: 'var(--tlens-spacing-md, 1rem)',
    flexWrap: 'wrap',
  },
  paginationButton: {
    padding: 'var(--tlens-spacing-xs, 0.5rem) var(--tlens-spacing-md, 1rem)',
    backgroundColor: 'var(--tlens-primary, #3498db)',
    color: 'white',
    border: 'none',
    borderRadius: 'var(--tlens-radius, 4px)',
    cursor: 'pointer',
  },
  paginationButtonDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  paginationInfo: {
    padding: '0 var(--tlens-spacing-md, 1rem)',
    fontWeight: 500,
  },
  paginationInput: {
    width: '60px',
    padding: 'var(--tlens-spacing-xs, 0.5rem)',
    textAlign: 'center',
    border: '1px solid var(--tlens-border, #ddd)',
    borderRadius: 'var(--tlens-radius, 4px)',
  },
  pageSize: {
    padding: 'var(--tlens-spacing-xs, 0.5rem)',
    border: '1px solid var(--tlens-border, #ddd)',
    borderRadius: 'var(--tlens-radius, 4px)',
    cursor: 'pointer',
  },
  info: {
    textAlign: 'center',
    color: 'var(--tlens-text-secondary, #666)',
    fontSize: 'var(--tlens-font-size-sm, 0.875rem)',
  },
  srOnly: {
    position: 'absolute',
    width: '1px',
    height: '1px',
    padding: 0,
    margin: '-1px',
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: 0,
  },
  tableSelectorSidebar: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--tlens-spacing-sm, 0.5rem)',
    padding: 'var(--tlens-spacing-md, 1rem)',
    borderRight: '1px solid var(--tlens-border, #ddd)',
    minWidth: '200px',
  },
  tableSelectorSidebarLabel: {
    fontWeight: 600,
    marginBottom: 'var(--tlens-spacing-sm, 0.5rem)',
  },
  tableSelectorSidebarButton: {
    padding: 'var(--tlens-spacing-xs, 0.5rem)',
    textAlign: 'left',
    backgroundColor: 'var(--tlens-bg-white, white)',
    border: '1px solid var(--tlens-border, #ddd)',
    borderRadius: 'var(--tlens-radius, 4px)',
    cursor: 'pointer',
  },
  tableSelectorSidebarButtonActive: {
    backgroundColor: 'var(--tlens-bg-sorted, #e9ecef)',
  },
  sortableHover: {
    opacity: 0.3,
  },
  filterColumnSelectorButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 'var(--tlens-spacing-xs, 0.5rem) var(--tlens-spacing-md, 1rem)',
    backgroundColor: 'var(--tlens-bg-header, #f8f9fa)',
    border: '1px solid var(--tlens-border, #ddd)',
    borderRadius: 'var(--tlens-radius, 4px)',
    cursor: 'pointer',
    fontSize: 'var(--tlens-font-size-sm, 0.875rem)',
    color: 'var(--tlens-text, #333)',
  },
  filterColumnSelectorDropdown: {
    backgroundColor: 'var(--tlens-bg-white, white)',
    border: '1px solid var(--tlens-border, #ddd)',
    borderRadius: 'var(--tlens-radius, 4px)',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    padding: 'var(--tlens-spacing-md, 1rem)',
    minWidth: '300px',
    maxWidth: '400px',
    zIndex: 1000,
  },
  filterColumnSelectorHeader: {
    display: 'flex',
    gap: '0.5rem',
    marginBottom: 'var(--tlens-spacing-md, 1rem)',
    paddingBottom: '0.5rem',
    borderBottom: '1px solid var(--tlens-border, #ddd)',
  },
  filterColumnSelectorAction: {
    padding: '0.25rem 0.5rem',
    fontSize: '0.75rem',
    backgroundColor: 'var(--tlens-bg-header, #f8f9fa)',
    border: '1px solid var(--tlens-border, #ddd)',
    borderRadius: 'var(--tlens-radius, 4px)',
    cursor: 'pointer',
    color: 'var(--tlens-text, #333)',
  },
  filterColumnSelectorActionDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  filterColumnSelectorList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    maxHeight: '300px',
    overflowY: 'auto',
    marginBottom: 'var(--tlens-spacing-md, 1rem)',
  },
  filterColumnSelectorItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.25rem 0',
    cursor: 'pointer',
  },
  filterColumnSelectorCheckbox: {
    width: '16px',
    height: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid var(--tlens-border, #ddd)',
    borderRadius: '2px',
    backgroundColor: 'var(--tlens-bg-white, white)',
    cursor: 'pointer',
  },
  filterColumnSelectorCheckboxChecked: {
    backgroundColor: 'var(--tlens-primary, #3498db)',
    borderColor: 'var(--tlens-primary, #3498db)',
  },
  filterColumnSelectorCheckboxIndicator: {
    color: 'white',
    fontSize: '12px',
    fontWeight: 'bold',
  },
  filterColumnSelectorColumnName: {
    flex: 1,
    fontSize: 'var(--tlens-font-size-sm, 0.875rem)',
    color: 'var(--tlens-text, #333)',
  },
  filterColumnSelectorColumnNameDefault: {
    fontWeight: 500,
  },
  filterColumnSelectorDefaultBadge: {
    fontSize: '0.625rem',
    backgroundColor: 'var(--tlens-bg-header, #f8f9fa)',
    color: 'var(--tlens-text-secondary, #666)',
    padding: '0.125rem 0.25rem',
    borderRadius: '2px',
    marginLeft: '0.5rem',
    textTransform: 'uppercase',
  },
  filterColumnSelectorFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.5rem',
    paddingTop: '0.5rem',
    borderTop: '1px solid var(--tlens-border, #ddd)',
  },
  filterColumnSelectorCancel: {
    padding: 'var(--tlens-spacing-xs, 0.5rem) var(--tlens-spacing-md, 1rem)',
    backgroundColor: 'var(--tlens-bg-white, white)',
    border: '1px solid var(--tlens-border, #ddd)',
    borderRadius: 'var(--tlens-radius, 4px)',
    cursor: 'pointer',
    fontSize: 'var(--tlens-font-size-sm, 0.875rem)',
    color: 'var(--tlens-text, #333)',
  },
  filterColumnSelectorApply: {
    padding: 'var(--tlens-spacing-xs, 0.5rem) var(--tlens-spacing-md, 1rem)',
    backgroundColor: 'var(--tlens-primary, #3498db)',
    border: '1px solid var(--tlens-primary, #3498db)',
    borderRadius: 'var(--tlens-radius, 4px)',
    cursor: 'pointer',
    fontSize: 'var(--tlens-font-size-sm, 0.875rem)',
    color: 'white',
  },
  filterColumnSelectorApplyDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  emptyStateButton: {
    marginTop: 'var(--tlens-spacing-xs, 0.5rem)',
    padding: 'var(--tlens-spacing-xs, 0.5rem) var(--tlens-spacing-md, 1rem)',
    backgroundColor: 'var(--tlens-primary, #3498db)',
    color: 'white',
    border: 'none',
    borderRadius: 'var(--tlens-radius, 4px)',
    cursor: 'pointer',
  },
  errorIcon: {
    fontSize: '2rem',
    opacity: 0.8,
  },
  errorTitle: {
    margin: 0,
    fontSize: '1.1rem',
    fontWeight: 600,
    color: 'var(--tlens-error, #c33)',
  },
  errorMessage: {
    margin: '0.5rem 0 0 0',
    fontSize: '0.95rem',
    lineHeight: 1.5,
  },
  errorContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    marginBottom: 'var(--tlens-spacing-md, 1rem)',
  },
};
